// The one-line language offer (unify-ui ship 3, piece f, ruling R21): on the ENGLISH page only, a
// browser set to one of the other four gets one dismissible line, written in that language — never
// a redirect, no timer. Ship 3 stores one family of things on a device: the dismissal,
// localStorage "filasophy.lang-offer.<id>" = "1", and a language picked in the switch,
// "filasophy.lang-offer" = "<id>" (switch.js) — either keeps the line away (OFFER_KEY names the stem).
// Tool pages mount it from their main.js; the hub (no script of its own) loads the built
// offer-entry, content-hashed (/ui/offer-XXXXXXXX.js) — one source, two deliveries.
import { LANGUAGES, pageLanguage, hrefFor } from './locales.js';
import { OFFER_WORDS } from './offer-words.js';      // the hub's build swaps it for its strings (plan change 6)

export const OFFER_KEY = 'filasophy.lang-offer';

// navigator.languages → the language to offer, or null: the FIRST tag the browser names that maps
// to one of the four. zh-Hans/CN/SG (and bare zh — its majority script) → zh-cn; zh-Hant/TW/HK/MO
// → zh-tw. English and anything unmapped offer nothing.
export function browserOfferLanguage(langs) {
  for (const raw of Array.isArray(langs) ? langs : [langs]) {
    const t = String(raw ?? '').toLowerCase();
    if (!t) continue;
    if (t === 'th' || t.startsWith('th-')) return 'th';
    if (t === 'ja' || t.startsWith('ja-')) return 'ja';
    if (t === 'zh' || t.startsWith('zh-')) return /hant|tw|hk|mo/.test(t) ? 'zh-tw' : 'zh-cn';
  }
  return null;
}

// Mount the line on THIS page (idempotent: a second call mounts nothing). Gated to English pages,
// to a mapped browser language, and to a dismissal not already stored. A localStorage that throws
// (private mode) does not stop the line — it only means the dismissal will not stick.
// linkFor (a tool's page): the same function its switch takes — the link is rebuilt through it when it
// is followed, so the design travels (an old link opened on the English page keeps its design in the
// offered language); the hub passes none and keeps the plain address.
export function mountLangOffer(win, { linkFor = null } = {}) {
  const doc = win.document;
  if (doc.getElementById('lang-offer')) return;
  if (pageLanguage().id !== 'en') return;               // the offer is an English-page thing only (pageLanguage: the record, .id the string)
  const id = browserOfferLanguage(win.navigator.languages);
  if (!id) return;
  const key = `${OFFER_KEY}.${id}`;
  let dismissed = null;
  // a dismissal of this language's line, or a language the visitor PICKED in the switch (any: the choice is theirs)
  try { dismissed = win.localStorage.getItem(key) ?? win.localStorage.getItem(OFFER_KEY); } catch { /* private mode: show it */ }
  if (dismissed !== null) return;
  const L = LANGUAGES.find((l) => l.id === id);
  const line = OFFER_WORDS.lines[id];                   // the OFFERED language's own words
  const dismiss = OFFER_WORDS.dismiss;                  // the page's words: the line mounts on English pages only
  if (typeof line !== 'string') return;

  const p = doc.createElement('p');
  p.id = 'lang-offer'; p.className = 'ui-offer'; p.lang = L.lang;   // the line carries its own language (piece m's net)
  const a = doc.createElement('a');
  a.href = hrefFor(id, win.location.pathname);          // the same page at its address in that language
  // the design is the page's NOW (the doc may not exist at mount): the href is rebuilt on every way a link is opened —
  // a press, a key's focus, a middle click, a click (a long-press or "open in new tab" starts from the press or the focus;
  // the final review's M1). The hub passes no linkFor and keeps the plain address.
  const carry = () => { if (linkFor) a.setAttribute('href', linkFor(new URL(hrefFor(id, win.location.pathname), win.location.href).toString())); };
  // following the offer is a pick: the language is stored under the switch's key, so the next English page offers nothing
  const follow = () => { carry(); try { win.localStorage.setItem(OFFER_KEY, id); } catch { /* private mode: it will honestly show again */ } };
  for (const t of ['pointerdown', 'focus']) a.addEventListener(t, carry);
  for (const t of ['click', 'auxclick']) a.addEventListener(t, follow);
  a.textContent = line;
  const x = doc.createElement('button');
  x.type = 'button'; x.className = 'ui-offer-off'; x.setAttribute('aria-label', dismiss); x.textContent = '×';
  x.addEventListener('click', () => {
    try { win.localStorage.setItem(key, '1'); } catch { /* private mode: it will honestly show again */ }
    p.remove();
  });
  p.append(a, x);
  const header = doc.querySelector('header.ui-header');
  if (header) header.after(p); else doc.body.prepend(p);   // tools: under the header; hub: top of the page
}
